import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import { Button } from "../components/Buttons";
import {
  getPayoutChargeConfig,
  setPayoutChargeConfig,
  getCashfreeFee,
  setCashfreeFee,
} from "../api";

const INR = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

/**
 * Admin config for Gmax payout charges. Slab-based: each slab charges a flat
 * fee + GST on that fee, applied on top of the withdrawal amount.
 */
export default function AdminPayoutCharges() {
  const [slabs, setSlabs] = useState([]);
  const [adminCharge, setAdminCharge] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  /* Cashfree payin fee % (separate setting, used for admin payin margin). */
  const [cashfreeFee, setCashfreeFeeState] = useState(1.6);
  const [savingFee, setSavingFee] = useState(false);

  useEffect(() => {
    getPayoutChargeConfig()
      .then(({ data }) => {
        setSlabs(data.slabs || []);
        setAdminCharge(data.adminChargeFlat || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    getCashfreeFee()
      .then(({ data }) => setCashfreeFeeState(data.percent ?? 1.6))
      .catch(() => {});
  }, []);

  async function handleSaveFee() {
    setSavingFee(true);
    setFeedback(null);
    try {
      const { data } = await setCashfreeFee(Number(cashfreeFee) || 0);
      setCashfreeFeeState(data.percent ?? 0);
      setFeedback({ severity: "success", msg: "Cashfree payin fee saved." });
    } catch (e) {
      setFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Could not save",
      });
    } finally {
      setSavingFee(false);
    }
  }

  const updateSlab = (i, key, value) =>
    setSlabs((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, [key]: value } : s)),
    );

  const addSlab = () =>
    setSlabs((prev) => [...prev, { min: 0, max: 0, flat: 0, gstPercent: 18 }]);

  const removeSlab = (i) =>
    setSlabs((prev) => prev.filter((_, idx) => idx !== i));

  const chargeFor = (s) => {
    const flat = Number(s.flat) || 0;
    return flat + (flat * (Number(s.gstPercent) || 0)) / 100;
  };

  async function handleSave() {
    setSaving(true);
    setFeedback(null);
    try {
      const { data } = await setPayoutChargeConfig({
        slabs: slabs.map((s) => ({
          min: Number(s.min) || 0,
          max: Number(s.max) || 0,
          flat: Number(s.flat) || 0,
          gstPercent: Number(s.gstPercent) || 0,
        })),
        adminChargeFlat: Number(adminCharge) || 0,
      });
      setSlabs(data.slabs || []);
      setAdminCharge(data.adminChargeFlat || 0);
      setFeedback({ severity: "success", msg: "Payout charges saved." });
    } catch (e) {
      setFeedback({
        severity: "error",
        msg: e.response?.data?.error || "Could not save",
      });
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Typography sx={{ p: 3 }}>Loading…</Typography>;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 0.5 }}>
        Payout Charges
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Each slab is <b>Gmax's cost</b> for that amount range (flat + GST). Your
        fixed <b>admin charge</b> is added on top. The user pays
        <b> Gmax cost + admin charge</b> on top of their withdrawal, and the
        admin charge is your profit.
      </Typography>

      {feedback && (
        <Alert
          severity={feedback.severity}
          sx={{ mb: 2 }}
          onClose={() => setFeedback(null)}
        >
          {feedback.msg}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Min ₹</TableCell>
                  <TableCell>Max ₹</TableCell>
                  <TableCell>Gmax flat ₹</TableCell>
                  <TableCell>GST %</TableCell>
                  <TableCell>Gmax cost</TableCell>
                  <TableCell>User pays (+ admin)</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {slabs.map((s, i) => (
                  <TableRow key={i}>
                    {["min", "max", "flat", "gstPercent"].map((k) => (
                      <TableCell key={k}>
                        <TextField
                          type="number"
                          size="small"
                          value={s[k]}
                          onChange={(e) => updateSlab(i, k, e.target.value)}
                          sx={{ width: 110 }}
                        />
                      </TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 600 }}>
                      {INR(chargeFor(s))}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {INR(chargeFor(s) + (Number(adminCharge) || 0))}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removeSlab(i)}
                      >
                        <DeleteRoundedIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Button
            variant="text"
            color="primary"
            startIcon={<AddRoundedIcon />}
            onClick={addSlab}
            sx={{ mt: 1 }}
          >
            Add slab
          </Button>

          <Stack
            direction="row"
            alignItems="center"
            gap={2}
            sx={{ mt: 3, flexWrap: "wrap" }}
          >
            <TextField
              type="number"
              size="small"
              label="Admin charge (fixed) ₹"
              value={adminCharge}
              onChange={(e) => setAdminCharge(e.target.value)}
              helperText="Your fixed fee on top of Gmax's cost — this is your profit"
              sx={{ maxWidth: 260 }}
            />
            <Button
              variant="contained"
              color="primary"
              loading={saving}
              onClick={handleSave}
              sx={{ alignSelf: "flex-start" }}
            >
              Save Charges
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Cashfree payin fee — used to compute the admin's payin margin. */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
            Cashfree Payin Fee
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            What Cashfree charges on payins (% of volume). Reports subtract this
            from commission to show the admin's real payin margin.
          </Typography>
          <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
            <TextField
              type="number"
              size="small"
              label="Cashfree fee %"
              value={cashfreeFee}
              onChange={(e) => setCashfreeFeeState(e.target.value)}
              sx={{ maxWidth: 200 }}
            />
            <Button
              variant="contained"
              color="primary"
              loading={savingFee}
              onClick={handleSaveFee}
            >
              Save Fee
            </Button>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
